import { HTMLAttributes, KeyboardEvent, useRef, useState } from "react";
import styled from "styled-components";
import { PositionProps, position, LayoutProps } from "styled-system";
import css from "@styled-system/css";
import { useHover } from "@react-aria/interactions";

import Box from "../Box/Box";
import { BoxProps } from "../Box/box.type";
import Icon from "../Icon/Icon";
import { AnalyticsProps } from "../analytics.types";
import { useResizeObserver } from "../useResizeObserver";

export interface VideoThumbnailProps {
  width: LayoutProps["width"];
  height: LayoutProps["height"];
  thumbnailUrl: string;
  onClick?: () => void;
  id?: string;
  videoTitle?: string;
  analytics?: VideoThumbnailAnalyticsProps;
}

export interface VideoThumbnailAnalyticsProps extends AnalyticsProps {
  eventLabel?: string;
}

const ThumbnailContainer = styled(Box)<BoxProps & { thumbnailUrl: string }>`
  cursor: pointer;
  position: relative;
  overflow: hidden;
  background-size: cover;
  background-position: 50%;
  background-repeat: no-repeat;
  ${(props) =>
    css({ borderRadius: 2, backgroundImage: `url(${props.thumbnailUrl})` })}
`;

const ThumbnailOverlay = styled(Box)<PositionProps>`
  opacity: 0.2;
  animation: 0.7s scale-up-center;

  @keyframes scale-down-center {
    0% {
      -webkit-transform: scale(1);
      transform: scale(1);
    }
    100% {
      -webkit-transform: scale(0);
      transform: scale(0);
    }
  }

  @keyframes scale-up-center {
    0% {
      border-radius: 100%;
      transform: scale(0);
    }
    100% {
      border-radius: 100%;
      transform: scale(1.4);
    }
  }

  ${css({
    backgroundColor: "background-dark-color",
    "&.hover": {
      animation: "0.5s scale-down-center",
      transform: "scale(0)",
      borderRadius: "100%",
    },
  })}
  ${position}
`;

const PlayButton = styled(Box)`
  width: 75px;
  height: 75px;
  border-radius: 100%;
  transform: translateX(-50%) translateY(-50%);
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-left: 6px;
  ${css({
    backgroundColor: "background-default-color",
    "&.hover": {
      backgroundColor: "background-dark-color",
    },
  })}
`;

const VideoThumbnail = ({
  thumbnailUrl,
  width,
  height,
  onClick,
  videoTitle = "",
  analytics,
  ...props
}: VideoThumbnailProps) => {
  const [buttonClassName, setButtonClassName] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const [thumbnailSizes] = useResizeObserver(containerRef);
  const { hoverProps, isHovered } = useHover({});

  const getOverlaySizes = () => {
    if (thumbnailSizes) {
      const { width, height } = thumbnailSizes;

      return width >= height
        ? { width, height: width }
        : { height, width: height };
    }
  };

  const overlayTopPosition =
    thumbnailSizes && thumbnailSizes.width > thumbnailSizes.height
      ? `calc((${thumbnailSizes.width}px - ${thumbnailSizes.height}px) / -2)`
      : 0;

  const onMouseEnter = () => {
    setButtonClassName("hover");
  };

  const onMouseLeave = () => {
    setTimeout(() => setButtonClassName(""), 300);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && onClick) {
      onClick();
    }
  };

  const playIconColor =
    buttonClassName === "hover"
      ? "background-default-color"
      : "background-dark-color";

  return (
    <ThumbnailContainer
      {...(hoverProps as Omit<HTMLAttributes<HTMLElement>, "css">)}
      data-en={analytics ? "click" : undefined}
      data-ec={analytics ? "video" : undefined}
      data-el={analytics?.eventLabel}
      data-ea={analytics?.eventAction}
      data-es={analytics?.eventSection}
      data-et={analytics?.eventType}
      data-ev={analytics?.eventValue}
      tabIndex={0}
      ref={containerRef}
      role="button"
      aria-label={`Play ${videoTitle} video`}
      width={width}
      height={height}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyDown={onKeyDown}
      thumbnailUrl={thumbnailUrl}
      {...props}
    >
      <PlayButton className={buttonClassName} aria-hidden="true" tabIndex={-1}>
        <Icon
          aria-hidden={true}
          name="play"
          weight="solid"
          color={playIconColor}
          width={31}
          height={31}
        />
      </PlayButton>
      <ThumbnailOverlay
        className={isHovered ? "hover" : ""}
        aria-hidden="true"
        position="absolute"
        width="100%"
        height="100%"
        color="black"
        top={overlayTopPosition}
        {...getOverlaySizes()}
      />
    </ThumbnailContainer>
  );
};

export default VideoThumbnail;
